/**
 * JWT Token utilities for MSP Platform authentication
 * Better than jwt-decode library since it's lighter and handles our specific needs
 */

interface JWTPayload {
  sub: string // User ID
  email: string
  'cognito:groups'?: string[] // User roles (optional)
  'custom:CompanyId'?: string
  'custom:AgencyId'?: string
  'custom:IsPlatform'?: string
  'custom:MemberId'?: string
  exp: number
  iat: number
}

/**
 * Decode JWT token without verification (for client-side parsing only)
 * Note: Token verification should be handled by the backend
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) return null

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    )

    return JSON.parse(jsonPayload) as JWTPayload
  } catch (error: unknown) {
    console.error('Failed to decode JWT token:', error)
    return null
  }
}

/**
 * Check if JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token)
  if (!payload) return true

  const currentTime = Math.floor(Date.now() / 1000)
  return payload.exp < currentTime
}

/**
 * Extract user information from JWT token
 */
export function extractUserFromToken(idToken: string): {
  userId: string
  emailAddress: string
  roles: string[]
  companyId?: string
  agencyId?: string
  memberId?: string
  isPlatform: boolean
} | null {
  const payload = decodeJWT(idToken)
  if (!payload) return null

  return {
    userId: payload.sub,
    emailAddress: payload.email,
    roles: payload['cognito:groups'] ?? [],
    companyId: payload['custom:CompanyId'],
    agencyId: payload['custom:AgencyId'],
    memberId: payload['custom:MemberId'],
    isPlatform: /true/i.test(payload['custom:IsPlatform'] ?? ''),
  }
}

/**
 * Async wrapper for decodeJWT (for compatibility with SHR patterns)
 * Note: This is still synchronous but returns a Promise for consistency
 */
export async function decodeJWTAsync(token: string): Promise<JWTPayload | null> {
  return decodeJWT(token)
}

/**
 * Async wrapper for extractUserFromToken (for compatibility with SHR patterns)
 */
export async function extractUserFromTokenAsync(idToken: string) {
  return extractUserFromToken(idToken)
}

/**
 * Create MSP auth response from token (for URL-based authentication)
 */
export function createAuthResponseFromToken(token: string): {
  idToken: string
  accessToken: string
  expiresIn: number
  refreshToken: string
  TokenType: string
  emailAddress: string
  userId: string
  isSuccess: boolean
  message: string
} | null {
  const userInfo = extractUserFromToken(token)
  if (!userInfo) return null

  return {
    idToken: token,
    accessToken: token, // In token-based auth, we might only have one token
    expiresIn: 3600, // Default expiration
    refreshToken: '',
    TokenType: 'Bearer',
    emailAddress: userInfo.emailAddress,
    userId: userInfo.userId,
    isSuccess: true,
    message: 'Token authentication successful',
  }
}
